import { useState, useEffect, useCallback, useRef } from 'react';
import { Clock, Play, Square, Coffee, QrCode, AlertTriangle, Check, Store, Loader2, RefreshCw, MapPin, Building2 } from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@easyflow/ui-frontend/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@easyflow/ui-frontend/components/ui/select';
import { DropdownMenuSeparator } from '@easyflow/ui-frontend/components/ui/dropdown-menu';
import { hrCurrentStatusSchema, hrAttendanceRowSchema, hrClockInInputSchema, hrQrCodeSchema } from '@easyflow/shared';
import { apiClient } from '@/lib/api';
import { useModules } from '@/contexts/ModuleContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { useToast } from '@/hooks/use-toast';
import { loadActiveHrStores } from '@/lib/hr-api';
import { getErrorMessage } from '../../lib/utils';
import type { HrCurrentStatus, HrAttendanceRow, StoreOption, HrClockInInput } from '@easyflow/shared';


type AttendanceRecord = HrAttendanceRow;
type ShiftInfo = NonNullable<HrCurrentStatus['shift']>;
type UserStore = HrCurrentStatus['user_stores'][number];

// Secondi lavorati della timbratura aperta, ricavati dagli orari del server e non contati
// a scatti: dopo un ricaricamento o in una scheda in secondo piano il tempo resta giusto.
// In pausa il conteggio si ferma all'inizio della pausa; total_break_minutes contiene
// solo le pause già chiuse.
const workedSeconds = (att: AttendanceRecord | null, now: number): number => {
  if (!att?.clock_in || (att.status !== 'active' && att.status !== 'on_break')) return 0;
  const end = att.status === 'on_break' && att.break_start ? new Date(att.break_start).getTime() : now;
  const elapsed = Math.floor((end - new Date(att.clock_in).getTime()) / 1000);
  return Math.max(0, elapsed - (att.total_break_minutes || 0) * 60);
};

export default function HRClockWidget() {
  const { hasPermission, loading: permissionsLoading } = usePermissions();
  const canManageQr = !permissionsLoading && hasPermission('hr.qr.manage');
  const { isModuleEnabled } = useModules();
  const { toast } = useToast();
  const [attendance, setAttendance] = useState<AttendanceRecord | null>(null);
  const [shift, setShift] = useState<ShiftInfo | null>(null);
  const [userStores, setUserStores] = useState<UserStore[]>([]);
  const [allStores, setAllStores] = useState<StoreOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [storesError, setStoresError] = useState<string | null>(null);
  const [storesLoading, setStoresLoading] = useState(false);
  const storesRequestRef = useRef<Promise<StoreOption[]> | null>(null);
  const [showQrDialog, setShowQrDialog] = useState(false);
  const [showStoreSelector, setShowStoreSelector] = useState(false);
  const [selectedStoreId, setSelectedStoreId] = useState('');
  const [isForceOtherSede, setIsForceOtherSede] = useState(false);
  const [qrImageUrl, setQrImageUrl] = useState<string | null>(null);
  const [qrCountdown, setQrCountdown] = useState(0);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrStoreName, setQrStoreName] = useState('');
  const [qrError, setQrError] = useState<string | null>(null);
  const [clockResult, setClockResult] = useState<{ success: boolean; message: string } | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const qrRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const activeStoreIdRef = useRef<string | null>(null);
  // Polling stato presenze mentre la dialog QR è aperta: serve a
  // chiudere automaticamente la dialog quando lo smartphone scansiona
  // il QR e completa la timbratura lato backend.
  const qrStatusPollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const initialAttendanceIdRef = useRef<string | null>(null);
  const qrAutoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Sessione monotona: incrementata ad ogni apertura della dialog QR.
  // Le risposte di poll arrivate dopo una chiusura/riapertura vengono ignorate.
  const qrSessionIdRef = useRef(0);

  const loadStatus = useCallback(async () => {
    try {
      const statusRes = await apiClient.get('/hr/attendance/current-status');
      if (statusRes.success && statusRes.data) {
        const data = hrCurrentStatusSchema.parse(statusRes.data);
        setStatusError(null);
        setAttendance(data.attendance);
        setShift(data.shift);
        setUserStores(data.user_stores);
      }
    } catch (e: unknown) {
      setStatusError(getErrorMessage(e));
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  // L'elenco completo delle sedi (GET /stores, permesso settings.view) serve solo per
  // «Forza timbratura in altra sede» e per chi non ha sedi assegnate: si carica
  // quando serve, così un dipendente non vede un errore di permessi all'apertura del menu.
  // Un rifiuto resta un errore visibile (storesError), mai un elenco vuoto.
  // Una sola richiesta alla volta: un doppio clic riusa quella in corso.
  const loadAllStores = (): Promise<StoreOption[]> => {
    if (allStores.length > 0) return Promise.resolve(allStores);
    if (!storesRequestRef.current) {
      setStoresError(null);
      setStoresLoading(true);
      storesRequestRef.current = loadActiveHrStores()
        .then((stores) => { setAllStores(stores); return stores; })
        .finally(() => { storesRequestRef.current = null; setStoresLoading(false); });
    }
    return storesRequestRef.current;
  };

  const showStoresError = (err: unknown) => {
    setStoresError(getErrorMessage(err));
  };

  useEffect(() => {
    setTimerSeconds(workedSeconds(attendance, Date.now()));
    if (attendance?.status !== 'active') return;
    timerRef.current = setInterval(() => setTimerSeconds(workedSeconds(attendance, Date.now())), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [attendance]);

  const formatTimer = (secs: number) => {
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    const s = secs % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = () => {
    if (!attendance || attendance.status === 'completed') {
      if (attendance?.status === 'completed') return <Badge className="bg-blue-500/20 text-blue-700 dark:text-blue-300 border-blue-500/30">Completato</Badge>;
      // Con un turno di oggi e nessuna timbratura la persona non è «fuori turno»: non ha (ancora) timbrato.
      if (shift) return <Badge variant="outline" className="text-muted-foreground">Non timbrato</Badge>;
      return <Badge variant="outline" className="text-muted-foreground">Fuori turno</Badge>;
    }
    if (attendance.status === 'on_break') return <Badge className="bg-yellow-500/20 text-yellow-700 dark:text-yellow-300 border-yellow-500/30">In pausa</Badge>;
    if (attendance.is_out_of_shift) return <Badge variant="outline">Fuori turno</Badge>;
    return <Badge className="bg-green-500/20 text-green-700 dark:text-green-300 border-green-500/30">In turno</Badge>;
  };

  const getLateEarlyBadge = () => {
    if (!attendance || attendance.is_out_of_shift || attendance.late_minutes == null) return null;
    const diffMin = attendance.late_minutes;
    if (diffMin > 5) return <Badge className="bg-red-500/20 text-red-700 dark:text-red-300 text-[10px]">Ritardo +{diffMin}m</Badge>;
    return null;
  };

  const handleClockAction = async (action: string) => {
    setLoading(true);
    try {
      const res = await apiClient.post(`/hr/attendance/${action}`);
      if (res.success) {
        toast({ title: action === 'clock-out' ? 'Uscita registrata' : action === 'break-start' ? 'Pausa iniziata' : 'Pausa terminata' });
        await loadStatus();
      }
    } catch (e) {
      toast({ title: 'Errore', description: getErrorMessage(e), variant: 'destructive' });
    }
    setLoading(false);
  };

  const loadQrForStore = async (storeId: string) => {
    try {
      const res = await apiClient.get<{ qr_data: string; remaining_seconds: number; store_name: string }>(`/hr/qr-tokens/store/${storeId}/qr-code`);
      if (res.success && res.data) {
        setQrStoreName(res.data.store_name);
        setQrCountdown(res.data.remaining_seconds);
        const { default: QRCode } = await import('qrcode');
        const qr = hrQrCodeSchema.parse(res.data);
        const url = await QRCode.toDataURL(new URL(qr.qr_data, window.location.origin).href, {
          width: 300,
          margin: 2,
          color: { dark: '#000000', light: '#ffffff' },
        });
        setQrImageUrl(url);

        if (countdownRef.current) clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
          setQrCountdown(prev => {
            if (prev <= 1) {
              if (activeStoreIdRef.current) loadQrForStore(activeStoreIdRef.current);
              return 30;
            }
            return prev - 1;
          });
        }, 1000);
      }
    } catch (e) {
      setQrImageUrl(null);
      console.error('QR load error:', e);
      setQrError('Impossibile caricare il QR. Riprova.');
    }
  };

  const openQrDialog = async (storeId: string) => {
    const sessionId = ++qrSessionIdRef.current;
    setShowQrDialog(true);
    setQrLoading(true);
    setQrError(null);
    setQrImageUrl(null);
    setClockResult(null);
    activeStoreIdRef.current = storeId;
    // Baseline ID: leggo lo stato fresco dal server per evitare falsi positivi
    // dovuti a state React stale (es. una presenza già attiva non ancora vista).
    initialAttendanceIdRef.current = attendance?.id || null;
    try {
      const baselineRes = await apiClient.get<HrCurrentStatus>('/hr/attendance/current-status');
      if (qrSessionIdRef.current !== sessionId) return;
      if (baselineRes?.success && baselineRes.data) {
        const data = hrCurrentStatusSchema.parse(baselineRes.data);
        initialAttendanceIdRef.current = data.attendance?.id || null;
        setAttendance(data.attendance);
        setShift(data.shift);
        setUserStores(data.user_stores);
      }
    } catch {
    }

    try {
      if (!canManageQr) {
        setQrError('Scansiona il QR esposto nella sede con il tuo account. La generazione è riservata ai gestori.');
        setQrLoading(false);
        return;
      }
      await loadQrForStore(storeId);
      if (qrSessionIdRef.current !== sessionId) return;

      if (qrRefreshRef.current) clearInterval(qrRefreshRef.current);
      qrRefreshRef.current = setInterval(() => {
        if (activeStoreIdRef.current) loadQrForStore(activeStoreIdRef.current);
      }, 25000);

      if (qrStatusPollRef.current) clearInterval(qrStatusPollRef.current);
      qrStatusPollRef.current = setInterval(async () => {
        const mySession = qrSessionIdRef.current;
        try {
          const statusRes = await apiClient.get<HrCurrentStatus>('/hr/attendance/current-status');
          if (qrSessionIdRef.current !== mySession) return;
          if (!statusRes?.success || !statusRes.data) return;
          const data = hrCurrentStatusSchema.parse(statusRes.data);
          const newAtt = data.attendance;
          const initialId = initialAttendanceIdRef.current;
          const isNewClockIn =
            newAtt && newAtt.id && newAtt.id !== initialId &&
            (newAtt.status === 'active' || newAtt.status === 'on_break');
          if (isNewClockIn) {
            setAttendance(newAtt);
            setShift(data.shift);
            setUserStores(data.user_stores);
            setClockResult({ success: true, message: 'Timbratura registrata!' });
            if (navigator.vibrate) navigator.vibrate(200);
            if (qrStatusPollRef.current) { clearInterval(qrStatusPollRef.current); qrStatusPollRef.current = null; }
            if (qrRefreshRef.current) { clearInterval(qrRefreshRef.current); qrRefreshRef.current = null; }
            if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
            if (qrAutoCloseTimerRef.current) clearTimeout(qrAutoCloseTimerRef.current);
            qrAutoCloseTimerRef.current = setTimeout(() => {
              if (qrSessionIdRef.current === mySession) closeQrDialog();
            }, 1800);
          }
        } catch (e) {
          console.error('[HRClockWidget] QR status poll error:', e);
        }
      }, 3000);
    } catch (e) {
      if (qrSessionIdRef.current === sessionId) {
        setQrError(getErrorMessage(e) || 'Errore nel caricamento del QR');
      }
    }
    if (qrSessionIdRef.current === sessionId) setQrLoading(false);
  };

  const handleClockIn = async () => {
    if (shift?.store_id) {
      openQrDialog(shift.store_id);
      return;
    }
    setIsForceOtherSede(false);
    if (userStores.length > 0) {
      setShowStoreSelector(true);
      return;
    }
    try {
      const stores = await loadAllStores();
      if (stores.length > 0) setShowStoreSelector(true);
      else toast({ title: 'Nessuna sede disponibile', variant: 'destructive' });
    } catch (err: unknown) {
      showStoresError(err);
    }
  };

  const closeQrDialog = () => {
    // Invalida la sessione: eventuali risposte di poll ancora in volo verranno scartate.
    qrSessionIdRef.current++;
    setShowQrDialog(false);
    setQrImageUrl(null);
    setQrError(null);
    setClockResult(null);
    activeStoreIdRef.current = null;
    initialAttendanceIdRef.current = null;
    if (qrRefreshRef.current) { clearInterval(qrRefreshRef.current); qrRefreshRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
    if (qrStatusPollRef.current) { clearInterval(qrStatusPollRef.current); qrStatusPollRef.current = null; }
    if (qrAutoCloseTimerRef.current) { clearTimeout(qrAutoCloseTimerRef.current); qrAutoCloseTimerRef.current = null; }
    void loadStatus();
  };

  // Cleanup garantito su unmount: evita interval/timer orfani se il widget
  // viene smontato mentre la dialog QR è aperta.
  useEffect(() => {
    return () => {
      qrSessionIdRef.current++;
      if (qrRefreshRef.current) clearInterval(qrRefreshRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (qrStatusPollRef.current) clearInterval(qrStatusPollRef.current);
      if (qrAutoCloseTimerRef.current) clearTimeout(qrAutoCloseTimerRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);

  const handleForceOtherSede = async () => {
    // Se la dialog QR viene chiusa (o la timbratura riesce) durante l'attesa, la
    // sessione cambia: la risposta tardiva non deve riaprire il selettore.
    const sessionId = qrSessionIdRef.current;
    try {
      await loadAllStores();
    } catch (err: unknown) {
      if (qrSessionIdRef.current === sessionId) showStoresError(err);
      return;
    }
    if (qrSessionIdRef.current !== sessionId) return;
    setIsForceOtherSede(true);
    setSelectedStoreId('');
    closeQrDialog();
    setShowStoreSelector(true);
  };

  const handleStoreSelected = () => {
    setShowStoreSelector(false);
    openQrDialog(selectedStoreId);
  };

  const handleManualClockIn = async () => {
    setLoading(true);
    setClockResult(null);
    try {
      const storeId = activeStoreIdRef.current || shift?.store_id || selectedStoreId;
      const body: HrClockInInput = { store_id: storeId || undefined };

      if ('geolocation' in navigator) {
        try {
          const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { enableHighAccuracy: true, timeout: 10000 });
          });
          body.latitude = pos.coords.latitude;
          body.longitude = pos.coords.longitude;
        } catch {
        }
      }

      const res = await apiClient.post('/hr/attendance/clock-in', hrClockInInputSchema.parse(body));
      if (res.success) {
        const isAnomaly = hrAttendanceRowSchema.parse(res.data).is_out_of_shift;
        setClockResult({
          success: true,
          message: isAnomaly
            ? 'Timbratura registrata (fuori turno — segnalata come anomalia)'
            : 'Timbratura registrata!'
        });
        if (navigator.vibrate) navigator.vibrate(200);
        await loadStatus();
        setTimeout(() => closeQrDialog(), 2000);
      }
    } catch (e) {
      setClockResult({ success: false, message: getErrorMessage(e) || 'Errore nella timbratura' });
    }
    setLoading(false);
  };

  if (!isModuleEnabled('hr')) return null;

  const currentStatus = attendance?.status || 'none';
  const storeOptions = isForceOtherSede ? allStores : (userStores.length > 0 ? userStores.map(s => ({ id: s.store_id, name: s.store_name })) : allStores);

  return (
    <>
      <DropdownMenuSeparator />
      <div className="px-2 py-2 space-y-2">
        {statusError && <div role="alert" className="text-xs text-destructive">
          Stato presenze non disponibile: {statusError}
          <Button variant="link" size="sm" onClick={() => void loadStatus()}>Riprova</Button>
        </div>}
        {storesError && <p role="alert" className="text-xs text-destructive">Elenco sedi non disponibile: {storesError}</p>}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">Turno</span>
          </div>
          {statusError ? <Badge variant="outline">Stato non disponibile</Badge> : getStatusBadge()}
        </div>

        {shift && (
          <div className="text-[11px] text-muted-foreground bg-muted/50 rounded px-2 py-1.5">
            <div className="font-medium text-foreground">{shift.shift_name} {shift.start_time?.substring(0,5)}-{shift.end_time?.substring(0,5)}</div>
            {shift.store_name && <div className="flex items-center gap-1 mt-0.5"><Store className="h-3 w-3" />{shift.store_name}</div>}
          </div>
        )}

        {!statusError && !shift && !attendance && (
          <div className="text-[11px] text-muted-foreground">Nessun turno pianificato</div>
        )}

        {(currentStatus === 'active' || currentStatus === 'on_break') && (
          <div className="flex items-center justify-between">
            <span className="text-lg font-mono font-bold text-foreground">{formatTimer(timerSeconds)}</span>
            {getLateEarlyBadge()}
          </div>
        )}

        {attendance?.status === 'completed' && attendance.total_work_minutes != null && (
          <div className="text-[11px] text-muted-foreground">
            Totale: {Math.floor(attendance.total_work_minutes / 60)}h {Math.round(attendance.total_work_minutes % 60)}m
          </div>
        )}

        <div className="flex gap-1.5">
          {(currentStatus === 'none' || currentStatus === 'completed') && (
            <Button size="sm" className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => void handleClockIn()} disabled={loading || storesLoading || !!statusError}>
              {loading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Play className="h-3 w-3 mr-1" />}
              Timbra Ingresso
            </Button>
          )}
          {currentStatus === 'active' && (
            <>
              <Button size="sm" variant="outline" className="flex-1 h-7 text-xs border-yellow-500 text-yellow-600 hover:bg-yellow-50 dark:hover:bg-yellow-900/20" onClick={() => handleClockAction('break-start')} disabled={loading || !!statusError}>
                <Coffee className="h-3 w-3 mr-1" />Pausa
              </Button>
              <Button size="sm" className="flex-1 h-7 text-xs bg-red-600 hover:bg-red-700 text-white" onClick={() => handleClockAction('clock-out')} disabled={loading || !!statusError}>
                <Square className="h-3 w-3 mr-1" />Uscita
              </Button>
            </>
          )}
          {currentStatus === 'on_break' && (
            <Button size="sm" className="flex-1 h-7 text-xs bg-green-600 hover:bg-green-700 text-white" onClick={() => handleClockAction('break-end')} disabled={loading || !!statusError}>
              <Play className="h-3 w-3 mr-1" />Fine Pausa
            </Button>
          )}
        </div>
      </div>

      <Dialog open={showStoreSelector} onOpenChange={setShowStoreSelector}>
        <DialogContent className="sm:max-w-md max-w-[95vw] bg-background">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {isForceOtherSede ? 'Forza Timbratura — Altra Sede' : 'Seleziona Sede'}
            </DialogTitle>
            <DialogDescription>
              {isForceOtherSede
                ? 'Stai timbrando in una sede diversa da quella del tuo turno. Verrà segnalata come anomalia.'
                : 'Scegli la sede dove stai timbrando'}
            </DialogDescription>
          </DialogHeader>
          {isForceOtherSede && (
            <div className="flex items-center gap-2 p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-xs text-amber-700 dark:text-amber-300">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              Questa timbratura verrà segnalata come anomalia all'amministratore
            </div>
          )}
          <Select value={selectedStoreId} onValueChange={setSelectedStoreId}>
            <SelectTrigger><SelectValue placeholder="Seleziona sede..." /></SelectTrigger>
            <SelectContent>
              {storeOptions.map((s) => (
                <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={handleStoreSelected} disabled={!selectedStoreId} className="w-full gap-2">
            <QrCode className="h-4 w-4" />Continua
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={showQrDialog} onOpenChange={(open) => { if (!open) closeQrDialog(); }}>
        <DialogContent className="sm:max-w-md max-w-[95vw] mx-auto bg-background">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              Timbratura {qrStoreName ? `— ${qrStoreName}` : ''}
            </DialogTitle>
            <DialogDescription className="text-center">
              {!shift && 'Fuori turno — '}Il QR registra la persona autenticata sul dispositivo che lo scansiona
            </DialogDescription>
          </DialogHeader>

          {clockResult ? (
            <div className={`flex flex-col items-center gap-3 py-6 ${clockResult.success ? 'text-green-600' : 'text-red-600'}`}>
              {clockResult.success ? <Check className="h-12 w-12" /> : <AlertTriangle className="h-12 w-12" />}
              <p className="text-center font-medium">{clockResult.message}</p>
            </div>
          ) : qrLoading ? (
            <div className="flex flex-col items-center gap-3 py-10">
              <Loader2 className="h-10 w-10 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Caricamento QR...</p>
            </div>
          ) : qrError ? (
            <div className="flex flex-col items-center gap-4 py-6">
              <AlertTriangle className="h-10 w-10 text-amber-500" />
              <p className="text-center text-sm text-muted-foreground">{qrError}</p>
              <Button onClick={handleManualClockIn} disabled={loading} className="w-full bg-green-600 hover:bg-green-700 text-white gap-2">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                Timbratura Manuale con GPS
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-1">
              {qrImageUrl && (
                <div className="bg-white p-3 rounded-xl shadow-lg">
                  <img src={qrImageUrl} alt="QR Timbratura" className="w-52 h-52 sm:w-64 sm:h-64" />
                </div>
              )}
              <div className="flex items-center gap-2 text-sm">
                <RefreshCw className={`h-4 w-4 ${qrCountdown <= 5 ? 'text-red-500 animate-spin' : 'text-muted-foreground'}`} />
                <span className={`font-mono font-bold ${qrCountdown <= 5 ? 'text-red-500' : 'text-foreground'}`}>
                  {qrCountdown}s
                </span>
                <span className="text-xs text-muted-foreground">aggiornamento</span>
              </div>

              <div className="w-full border-t border-border pt-3 space-y-2">
                <p className="text-xs text-center text-muted-foreground">
                  Se stai usando l'app dallo smartphone:
                </p>
                <Button onClick={handleManualClockIn} disabled={loading} variant="outline" className="w-full gap-2">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <MapPin className="h-4 w-4" />}
                  Timbra con GPS
                </Button>

                <Button onClick={() => void handleForceOtherSede()} disabled={storesLoading} variant="ghost" size="sm" className="w-full text-xs text-muted-foreground gap-1.5">
                  <Building2 className="h-3.5 w-3.5" />
                  Forza timbratura in altra sede
                </Button>
                {storesError && <p role="alert" className="text-xs text-center text-destructive">Elenco sedi non disponibile: {storesError}</p>}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
